/* eslint-disable react-hooks/exhaustive-deps */
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Box, Flex, Button, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { gql, useMutation } from '@apollo/client';
import { useEffect } from 'react';
import DatetimeFormField from '../../components/form/DatetimeFormField';
import RadioValueFormField from '../../components/form/RadioValueFormField';
import FileFormField from '../../components/form/FileFormField';
import TextAreaFormField from '../../components/form/TextAreaFormField';
import moment from 'moment';
import c from '../../constant';
import * as Yup from 'yup';
import TextFormField from '../form/TextFormField';

const M_UPDATE = gql`
	mutation (
		$editableDescription: String
		$editableDueDate: DateTime
		$proposalNumber: String
		$npReportNumber: String
		$note: String
		$attachmentFile: Upload
		$attachmentFileSize: Float
		$isEditable: Boolean!
		$type: MDDataReportEnumType!
		$id: String!
	) {
		setMonthlyReportEditableStatus(
			editableDescription: $editableDescription
			editableDueDate: $editableDueDate
			proposalNumber: $proposalNumber
			npReportNumber: $npReportNumber
			note: $note
			attachmentFile: $attachmentFile
			attachmentFileSize: $attachmentFileSize
			isEditable: $isEditable
			type: $type
			id: $id
		)
	}
`;

const MAX_FILE_SIZE = 104857600;

const validationSchema = Yup.object().shape({
	isEditable: Yup.string().required('required'),
	editableDueDate: Yup.mixed().when('isEditable', {
		is: (val) => val === '1',
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	editableDescription: Yup.string().nullable(),
	proposalNumber: Yup.string().nullable(),
	npReportNumber: Yup.string().nullable(),
	note: Yup.string().nullable(),
	attachmentFile: Yup.mixed().when('isEditable', {
		is: (val) => val === '1',
		then: Yup.mixed()
			.required('required')
			.test('fileSize', 'over limit', (value) => {
				if (!value) return false;
				return value?.size <= MAX_FILE_SIZE;
			}),
		otherwise: Yup.mixed().nullable(),
	}),
});

export default function EditMonthlyReportModal({ id = null, type = null, isEditable = false, onClose = () => null, onCallback = () => null }) {
	const toast = useToast();
	const { t } = useTranslation('common');
	const [setStatus, { loading, data, error }] = useMutation(M_UPDATE);

	useEffect(() => {
		if (data?.setMonthlyReportEditableStatus) {
			onClose();
			onCallback();
		}
	}, [data]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.forEach(({ message }) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	return (
		<Modal isOpen={true} onClose={onClose} closeOnEsc={!loading} closeOnOverlayClick={!loading}>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader>{t('Allow Editing')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Box p={4}>
						<Formik
							initialValues={{
								isEditable: isEditable === true ? '0' : '1',
								editableDueDate: moment().add(3, 'days').endOf('days'),
								editableDescription: '',
								proposalNumber: '',
								npReportNumber: '',
								note: '',
								attachmentFile: null,
							}}
							validationSchema={validationSchema}
							onSubmit={(values, { setFieldTouched, validateForm }) => {
								if (values?.isEditable === '1') {
									setFieldTouched('editableDueDate', true, true);
									setFieldTouched('attachmentFile', true, true);

									validateForm().then((errors) => {
										if (errors?.editableDueDate || errors?.attachmentFile) return;

										const variables = {
											id,
											type,
											isEditable: true,
											proposalNumber: values?.proposalNumber || null,
											npReportNumber: values?.npReportNumber || null,
											note: values?.note || null,
											attachmentFile: values?.attachmentFile || null,
											attachmentFileSize: values?.attachmentFile?.size || null,
											editableDueDate: values?.editableDueDate,
											editableDescription: values?.editableDescription || null,
										};

										setStatus({ variables });
									});
								} else {
									const variables = {
										id,
										type,
										isEditable: false,
										proposalNumber: null,
										npReportNumber: null,
										note: null,
										attachmentFile: null,
										attachmentFileSize: null,
										editableDueDate: null,
										editableDescription: null,
									};

									setStatus({ variables });
								}
							}}
						>
							{({ values, ...props }) => (
								<Form>
									<Flex pl={4} pr={4} border="2px dashed #c0392b" direction="column">
										<RadioValueFormField
											{...props}
											label="Permission"
											name="isEditable"
											radios={[
												{ label: 'Open', value: '1' },
												{ label: 'Close', value: '0' },
											]}
											required
										/>

										{values?.isEditable === '1' && (
											<>
												<DatetimeFormField {...props} label="Due Date" name="editableDueDate" required />
												<TextAreaFormField {...props} label="Description (Company)" name="editableDescription" />
												<Box width="100%" borderBottom="2px dashed #c0392b" mt={4} mb={6} />
												<TextFormField {...props} label="Proposal/Letter In" name="proposalNumber" />
												<TextFormField {...props} label="np or rp" name="npReportNumber" />
												<TextAreaFormField {...props} label="Note (Official)" name="note" />
												<FileFormField {...props} label="Document" name="attachmentFile" maxFileSize={MAX_FILE_SIZE} required />
											</>
										)}
									</Flex>

									<Flex mt="16px">
										<Button
											id="login-btn"
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											type="submit"
											borderRadius={c.borderRadius}
											color="#3498db"
											isDisabled={loading}
											isLoading={loading}
										>
											{t('Update')}
										</Button>
									</Flex>
								</Form>
							)}
						</Formik>
					</Box>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
